import { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import { VictoryChart, VictoryAxis, VictoryLine, VictoryTheme, Line } from 'victory'
import withStyles from 'beam/dist/styles'
import { withTranslation } from 'react-i18next'
import AudioEvent from './AudioEvent'
import EVENT_TYPE from '../../const/eventType'
import { isEmpty } from 'lodash'
import { difference } from '../../../../Common/Utils'

const styles = theme => ({
  svg: {
    display: 'block',
  },
  energyBorder: {
    stroke: theme.palette.grey[300],
  },
})

class AudioEnergyTape extends Component {
  static propTypes = {
    width: PropTypes.number.isRequired,
    graphHeight: PropTypes.number.isRequired,
    classes: PropTypes.object.isRequired,
    theme: PropTypes.object.isRequired,
    t: PropTypes.func,
    eventLanePadding: PropTypes.number.isRequired,
    areEventsRightAligned: PropTypes.bool,
    audioOffsetPx: PropTypes.number.isRequired,
    audioOffsetMs: PropTypes.number.isRequired,
    audioHeightPx: PropTypes.number.isRequired,
    heightUnitsPerMs: PropTypes.number.isRequired,
    strokeWidth: PropTypes.number.isRequired,
    chartData: PropTypes.array,
    minmax: PropTypes.number,
    energy: PropTypes.object,
    events: PropTypes.array,
    comments: PropTypes.array,
    setTime: PropTypes.func,
    speechHits: PropTypes.array,
    selectedEventType: PropTypes.string,
    showSpeechHits: PropTypes.bool,
  }

  static defaultProps = {
    energy: null,
    minmax: 0,
    events: [],
    comments: [],
    speechHits: [],
    chartData: [],
    t: str => str,
    selectedEventType: 'ALL',
    showSpeechHits: true,
    areEventsRightAligned: false,
    setTime: () => {},
  }

  shouldComponentUpdate(nextProps, nextState) {
    const propsChanged = !isEmpty(difference(nextProps, this.props))
    const stateChanged = !isEmpty(difference(nextState, this.state))
    return propsChanged || stateChanged
  }

  render() {
    const {
      width,
      graphHeight,
      classes,
      theme,
      t,
      eventLanePadding,
      areEventsRightAligned,
      audioOffsetPx,
      audioOffsetMs,
      audioHeightPx,
      heightUnitsPerMs,
      strokeWidth,
      chartData,
      minmax,
      energy,
      events,
      comments,
      setTime,
      speechHits,
      selectedEventType,
      showSpeechHits,
    } = this.props
    return (
      <svg width={width} height={graphHeight} className={classes.svg}>
        {areEventsRightAligned ? (
          <Line
            x1={width - eventLanePadding * 2}
            y1={0}
            x2={width - eventLanePadding * 2}
            y2={graphHeight}
            className={classes.energyBorder}
          />
        ) : (
          <Fragment>
            <Line
              x1={eventLanePadding}
              y1={0}
              x2={eventLanePadding}
              y2={graphHeight}
              className={classes.energyBorder}
            />
            <Line
              x1={width - eventLanePadding}
              y1={0}
              x2={width - eventLanePadding}
              y2={graphHeight}
              className={classes.energyBorder}
            />
          </Fragment>
        )}
        <VictoryChart
          padding={{
            top: audioOffsetPx,
            left: areEventsRightAligned ? 0 : eventLanePadding,
            right: areEventsRightAligned ? eventLanePadding * 2 : eventLanePadding,
          }}
          theme={VictoryTheme.material}
          width={width}
          height={audioHeightPx + audioOffsetPx}
          style={{
            parent: {
              height: '100%',
              width: '100%',
            },
          }}
          standalone={false}
        >
          {/* This removes axes from the graph */}
          <VictoryAxis
            dependentAxis
            invertAxis={true}
            style={{
              axis: { stroke: 'none', fill: 'none' },
              axisLabel: { stroke: 'none', fill: 'none' },
              grid: { stroke: 'none', fill: 'none' },
              ticks: { stroke: 'none', fill: 'none' },
              tickLabels: { stroke: 'none', fill: 'none' },
            }}
          />
          {/*
      sortKey="y" makes it so that the data is sorted vertically along the y axis
      TODO add colors from theme
      */}
          <VictoryLine
            style={{
              data: {
                stroke: theme.palette.color.red[700], //From http://confluence/display/TEAM/Calabrio+ONE+Color+Palette data visualization colors
                strokeWidth,
              },
            }}
            data={chartData}
            domain={{
              x: [-1 * minmax, minmax],
            }}
            x="channel_0"
            y="index"
            sortKey="y"
          />
          {energy.channel_1 && (
            <VictoryLine
              style={{
                data: {
                  stroke: theme.palette.color.lightBlue[800],
                  strokeWidth,
                },
              }}
              data={chartData}
              domain={{
                x: [-1 * minmax, minmax],
              }}
              x="channel_1"
              y="index"
              sortKey="y"
            />
          )}
        </VictoryChart>

        {events &&
          events
            .filter(event => [event.eventType.toLowerCase(), 'all'].indexOf(selectedEventType.toLowerCase()) !== -1)
            .map((event, i) => (
              <AudioEvent
                key={i}
                size={eventLanePadding}
                event={event}
                heightUnitsPerMs={heightUnitsPerMs}
                t={t}
                containerWidth={width}
                setTime={setTime}
                areEventsRightAligned={areEventsRightAligned}
              />
            ))}

        {comments &&
          [EVENT_TYPE.COMMENT, 'all'].indexOf(selectedEventType.toLowerCase()) !== -1 &&
          comments.map((comment, i) => (
            <AudioEvent
              key={i}
              size={eventLanePadding}
              event={comment}
              heightUnitsPerMs={heightUnitsPerMs}
              t={t}
              containerWidth={width}
              setTime={setTime}
              areEventsRightAligned={areEventsRightAligned}
            />
          ))}

        {showSpeechHits &&
          speechHits &&
          [EVENT_TYPE.TRANSCRIPTION_HIT, 'all'].indexOf(selectedEventType.toLowerCase()) !== -1 &&
          speechHits.map((hit, i) => (
            <AudioEvent
              key={i}
              size={eventLanePadding}
              event={{
                ...hit,
                contactOffset: audioOffsetMs + hit.start,
                duration: hit.stop - hit.start,
              }}
              heightUnitsPerMs={heightUnitsPerMs}
              t={t}
              containerWidth={width}
              eventMaxWidth={width - eventLanePadding * 2 /*Because of padding*/}
              setTime={setTime}
              areEventsRightAligned={areEventsRightAligned}
            />
          ))}
      </svg>
    )
  }
}

export { AudioEnergyTape as UnstyledAudioEnergyTape }

export default withTranslation()(withStyles(styles, { withTheme: true })(AudioEnergyTape))
